import { Card, Illustration, Text, type UseModalState } from "@clipboard-health/ui-components";
import { CardContent, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";

import { type WorkerRequirement } from "../../Documents/api/types";
import { RESUME_REQUIREMENT_ID } from "../../PlacementCandidate/components/PlacementCandidateOnboarding/ResumeCard";
import { type PlacementCandidateScreeningStatus } from "../../PlacementCandidate/types";
import { useRequestCandidateResume } from "../api/useRequestCandidateResume";

interface ShareYourResumeCardProps {
  modalState: UseModalState;
  resumeRequirement: WorkerRequirement;
  workerEmail: string;
  placementCandidateId: string;
  candidateScreeningStatus?: PlacementCandidateScreeningStatus;
  placementId: string;
  workplaceId: string;
}

export function ShareYourResumeCard(props: ShareYourResumeCardProps) {
  const {
    modalState,
    resumeRequirement,
    candidateScreeningStatus,
    placementCandidateId,
    placementId,
    workplaceId,
    workerEmail,
  } = props;

  const logContext = {
    placementCandidateId,
    candidateScreeningStatus,
    placementId,
    workplaceId,
  };

  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_SHARE_YOUR_RESUME_CARD_VIEWED, {
    resumeRequirement: { id: resumeRequirement.requirementId, status: resumeRequirement.status },
    ...logContext,
  });

  const { isLoading: isRequestCandidateResumeLoading, mutateAsync: requestCandidateResume } =
    useRequestCandidateResume();

  return (
    <Card
      sx={{
        p: 1.5,
        textAlign: "center",
        position: "relative",
        border: (theme) => `1px solid ${theme.palette.border?.primary}`,
      }}
    >
      <CardContent component={Stack} spacing={5} sx={{ alignItems: "center" }}>
        <Stack spacing={5} sx={{ alignItems: "center" }}>
          <Illustration type="document-upload" />
          <Text semibold variant="h5" sx={{ fontWeight: "bold" }}>
            Share your resume
          </Text>

          <Text
            variant="body1"
            sx={{
              maxWidth: "90%",
              alignSelf: "center",
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            Attach a resume so employers can see your background in more detail.
          </Text>
        </Stack>
        <Stack spacing={3}>
          <Button
            variant="contained"
            href={`/home-v2/documents/${RESUME_REQUIREMENT_ID}`}
            size="large"
            onClick={() => {
              logEvent(APP_V2_APP_EVENTS.PLACEMENT_SHARE_YOUR_RESUME_ACTIONS, {
                action: "upload-resume",
                ...logContext,
              });
            }}
          >
            Upload your resume
          </Button>
          <Text>or</Text>
          <LoadingButton
            variant="outlined"
            size="large"
            isLoading={isRequestCandidateResumeLoading}
            onClick={async () => {
              logEvent(APP_V2_APP_EVENTS.PLACEMENT_SHARE_YOUR_RESUME_ACTIONS, {
                action: "email-resume",
                email: workerEmail,
                ...logContext,
              });
              await requestCandidateResume({ placementCandidateId });
              modalState.openModal();
            }}
          >
            Email us your resume
          </LoadingButton>
        </Stack>
      </CardContent>
    </Card>
  );
}
